import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListSubheader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { MoreVert, Delete } from "@mui/icons-material";
import ContentPasteGoOutlinedIcon from "@mui/icons-material/ContentPasteGoOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DistributeQuestionsDialog from "./DistributeQuestionsDialog";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FolderCopyOutlinedIcon from "@mui/icons-material/FolderCopyOutlined";
import { getNextPath, getParentPath, NodeType } from "./Utils";
import {
  allStatus,
  convertFirstUpper,
  getServerEnvironment,
  statusType,
} from "../../store/slices/constants/staticContents";
import { updateRefetch } from "../../store/slices/userManagement";
import { useAppDispatch } from "../../hooks/storeHooks";
import PasteConfirmDialog from "./PasteConfirmDialog";
import { allApis } from "../../request/config";
import apiRequest from "../../request/apiRequest/apiRequest";
import { showSnack } from "../../store/slices/constants";
import useCopiedData from "../../store/slices/constants/staticContents/useCopiedData";
import DistributeQuestionstoClusters from "./DistributeQuestionstoClusters";
import useLastMockMoved from "../../store/slices/constants/staticContents/useLastMockMoved";

interface ActionsProps {
  onDelete: () => void;
  onCut: () => void;
  onStatus: (data: any) => void;
  row: any;
  type?: NodeType;
  disabled?: boolean;
  anchorEls?: any;
  setAnchorEls?: any;
  bulkaddQuestion?: any;
  bulkcopyQuestion?: any;
  handlePaste?: () => void;
  hierarchy?: any;
  setLoader?: any;
  onReFetch?: any;
}

const Actions: React.FC<ActionsProps> = ({
  onDelete,
  onCut,
  onStatus,
  row,
  type,
  disabled = false,
  anchorEls,
  setAnchorEls,
  bulkaddQuestion,
  bulkcopyQuestion,
  handlePaste,
  hierarchy,
  setLoader,
  onReFetch,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {setlastMock}=useLastMockMoved()
  const { copiedData, setCopiedData, clearCopiedData } = useCopiedData();
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [pasetQueToClusterDialog, setPasetQueToClusterDialog] = useState(false);
  const [pasteConfirmOpen, setPasteConfirmOpen] = useState(false);
  const open = Boolean(anchorEl);
  const dispatch = useAppDispatch();
  // Open menu
  const handleClick = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    setAnchorEls?.(null);
  };

  useEffect(() => {
    if (anchorEls?.e) setAnchorEl(anchorEls.e);
  }, [anchorEls?.e]);

  const getStatusButtons = (list: statusType[]) =>
    list.map((item: statusType) => ({
      label:
        typeof item.actionLabel === "function"
          ? item.actionLabel(item.label)
          : item.actionLabel,
      onClick: () => onStatus(item.label),
    }));

  const getDynamicActions = (): any[] => {
    if (!row?.status) return [];
    const status = row.status.toLowerCase();
    const find = (label: string) =>
      getStatusButtons(allStatus.filter((s) => s.label === label))[0];
    const actions: any[] = [];
    switch (status) {
      case "draft":
        actions.push(find("ready"));
        break;
      case "ready":
        actions.push(find("approved"), find("rejected"), find("draft"));
        break;
      case "approved":
        actions.push(find("published"), find("rejected"));
        break;
      case "published":
        actions.push(find("archived"), find("unpublished"));
        break;
      case "archived":
        actions.push(find("unpublished"));
        break;
      case "rejected":
        actions.push(find("draft"));
        break;
      case "unpublished":
        actions.push(find("archived"), find("published"));
        break;
    }
    return actions.filter(Boolean);
  };

  const staticMenuItems = [
    { label: "Delete", icon: <Delete fontSize="small" />, onClick: onDelete },
  ];

  const pasteMenuItem =
    type === "TOPIC" && copiedData.filter((s: any) => !s.parent).length > 0
      ? [
          {
            label: "Paste Mocktest Copied Questions",
            onClick: () => {
              setPasteDialogOpen(true);
              handleClose();
            },
            icon: <ContentPasteGoOutlinedIcon />,
          },
        ]
      : copiedData.filter((s: any) => s.parent && s.parent.type === type)
          .length > 0
      ? [
          {
            label: type
              ? `Paste Copied ${convertFirstUpper(
                  getNextPath(type ?? "").toLowerCase()
                )}`
              : `Paste`,
            onClick: () => {
              if (type === "CLUSTER") {
                setPasetQueToClusterDialog(true);
              } else {
                setPasteConfirmOpen(true);
              }
              handleClose();
            },
            icon: <ContentPasteGoOutlinedIcon />,
          },
        ]
      : [];

  const menuItems = [
    ...(bulkcopyQuestion
      ? [
          {
            label: "Import Questions from PYQs",
            icon: <FolderCopyOutlinedIcon fontSize="small" />,
            onClick: bulkcopyQuestion,
          },
        ]
      : []),
    ...(bulkaddQuestion
      ? [
          {
            label: "Import Questions from DOCX",
            icon: <FileUploadOutlinedIcon fontSize="small" />,
            onClick: bulkaddQuestion,
          },
        ]
      : []),
    type === "EXAM"
      ? false
      : {
          label: "Cut",
          icon: <ContentCopyOutlinedIcon fontSize="small" />,
          onClick: () => {
            onCut();
          },
        },
    ...pasteMenuItem,
    ...staticMenuItems,
    ...getDynamicActions(),
  ].filter((s) => s);
  const handleDelete = (idx: number) => {
    const updated = copiedData.filter((_, i) => i !== idx);
    setCopiedData(updated);
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(updated))));
    localStorage.setItem("copied", encoded);
  };
  const handleChildDelete = (parentIdx: number, idToRemove: any) => {
    const updated = copiedData.map((item, idx) => {
      if (idx !== parentIdx) return item;

      // 🔹 If it's a parent-type item
      if (item.parent && Array.isArray(item.childs)) {
        return {
          ...item,
          childs: item.childs.filter((child: any) => child.id !== idToRemove),
        };
      }

      // 🔹 If it's a question-type item
      if (Array.isArray(item.questions)) {
        return {
          ...item,
          questions: item.questions.filter((q: any) => q.id !== idToRemove),
        };
      }

      return item;
    });

    setCopiedData(updated);

    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(updated))));
    localStorage.setItem("copied", encoded);
  };
  const handlePasteConfirm = async (level: NodeType, move_ids: number[]) => {
    setLoader(true);
    const statusResponse: any = await apiRequest({
      api: allApis.move,
      payload: {
        level: level.toLowerCase(),
        move_ids: move_ids,
        parent_id: row?.id,
      },
      method: "PUT",
    });
    if (statusResponse && statusResponse.error) {
      setLoader(false);
      dispatch(
        showSnack({
          open: true,
          severity: "error",
          message: statusResponse.msg,
        })
      );
    } else {
      dispatch(
        showSnack({
          open: true,
          severity: "success",
          message: `${
            move_ids.length
          } ${level.toLowerCase()}s MOVED successfully`,
        })
      );

      if (onReFetch) {
        console.log(getParentPath(level), "getParentPath(level)");
        onReFetch(getParentPath(level));
      }
      setPasteConfirmOpen(false);
      clearCopiedData();

      setTimeout(() => {
        dispatch(updateRefetch(true));
      }, 1);

      setLoader(false);
    }
  };
  return (
    <Box>
      {type && pasteConfirmOpen && (
        <PasteConfirmDialog
          open={pasteConfirmOpen}
          type={type}
          destination={`${row?.name} #${row?.id}`}
          copiedData={copiedData}
          onClose={() => setPasteConfirmOpen(false)}
          onConfirm={handlePasteConfirm}
          onDelete={handleDelete}
          onChildDelete={handleChildDelete}
        />
      )}
      {!disabled && (
        <IconButton
          onClick={handleClick}
          sx={{
            p: "4px",
            borderRadius: "6px",
            "&:hover": { backgroundColor: "rgba(25,118,210,0.08)" },
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        anchorReference={anchorEls?.axis?.mouseY ? "anchorPosition" : undefined}
        anchorPosition={
          anchorEls?.axis?.mouseY
            ? { top: anchorEls.axis.mouseY, left: anchorEls.axis.mouseX }
            : undefined
        }
        onClose={handleClose}
        PaperProps={{
          sx: { minWidth: 200, borderRadius: 2, boxShadow: 3, py: 0.5 },
        }}
      >
        <ListSubheader>{`Action for ${convertFirstUpper(
          row?.name || "Selected"
        )} ${convertFirstUpper((type ?? "").toLowerCase() || "")}${
          row?.name ? "" : "s"
        }`}</ListSubheader>
        <Divider sx={{ my: 0.5 }} />
        {menuItems.map((item, index) => (
          <Box key={item.label}>
            <MenuItem
              onClick={() => {
                item.onClick?.();
                handleClose();
              }}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 1,
                px: 2,
                borderRadius: 1,
                "&:hover": { backgroundColor: "rgba(25,118,210,0.08)" },
              }}
            >
              <Typography fontSize="0.9rem" mr="10px">
                {item.label}
              </Typography>
              {item.icon}
            </MenuItem>
            {index < menuItems.length - 1 && <Divider />}
          </Box>
        ))}
      </Menu>
      <DistributeQuestionsDialog
        open={pasteDialogOpen}
        // onChildDelete={handleChildDelete}
        setLoader={setLoader}
        hierarchy={hierarchy}
        onClose={() => setPasteDialogOpen(false)}
        onPaste={(assigned) => {
          setLoader(false);
          if (onReFetch) {
            onReFetch(getParentPath(type ?? "TOPIC"));
          }
          setTimeout(() => {
            dispatch(updateRefetch(true));
          }, 1);
         setlastMock(copiedData)
          clearCopiedData();
          setPasteDialogOpen(false);
        }}
      />
      {pasetQueToClusterDialog && (
        <DistributeQuestionstoClusters
          open={pasetQueToClusterDialog}
          // onChildDelete={handleChildDelete}
          setLoader={setLoader}
          hierarchy={hierarchy}
          onClose={() => setPasetQueToClusterDialog(false)}
          onPaste={(assigned) => {
            setLoader(false);
            if (onReFetch) {
              onReFetch("CLUSTER");
            }
            setTimeout(() => {
              dispatch(updateRefetch(true));
            }, 1);

            clearCopiedData();
            setPasetQueToClusterDialog(false);
          }}
        />
      )}
    </Box>
  );
};

export default Actions;
